import React from 'react'
import '@ica-azure/ica-elements/IcaIconFilter'
import '@ica-azure/ica-elements/IcaIconError'
import SpinnerIcon from '../Icons/SpinnerIcon'
import { IcaButton } from '../IcaElements/IcaButton'
import { IcaCheckbox } from '../IcaElements/IcaCheckbox'
import { IcaDialog } from '../IcaElements/IcaDialog'
import { IcaHeader } from '../IcaElements/IcaHeader'
import { IcaPlaceholder } from '../IcaElements/IcaPlaceholder'
import PicklistFilter from './PicklistFilter'
import PicklistArticle from './PicklistArticle'
import InputCheckbox from '../InputCheckbox/InputCheckbox'
import PrintPicklist from './PrintPicklist'
import SiteSelector from './SiteSelector'
import { Store } from '../../contexts/store-context'
import { useDeviceContext } from '../../contexts/device-context'
import { arraysEqualContent, getSelectedItemsForPrint, groupAndFilterItems, PICK_DISPLAY_DEFAULT_OPTIONS, PickDisplayOption, SortingChangedEvent, STOCK_STATUS_ALL, STOCK_STATUS_RUNNING_OUT, STOCK_STATUS_OUT } from './picklistHelpers'
import { StoreActiveCategories, Item, SelectedArticles } from '../../types/Picklist'
import PrintPortal from '../PrintPortal'
import { IcaSortingDropDown } from '../IcaElements/IcaSortingDropDown'
import './Picklist.scss'
import { getFromStorage, setToStorage } from '../../utils/storageUtils'
import { PICK_DISPLAY_PREFERENCE_KEY } from '../../constants/localStorageKeys'

export interface IPickListProps {
  sites: Store[]
  items: Item[]
  categories: StoreActiveCategories[]
  loading: boolean
  error: boolean
  showPicklistDialog: boolean
  setShowPicklistDialog: (show: boolean) => void
}

export default function Picklist({
  sites,
  items,
  categories,
  loading,
  error,
  showPicklistDialog,
  setShowPicklistDialog,
}: IPickListProps) {
  const { deviceState } = useDeviceContext()
  const [allArticlesSelected, setAllArticlesSelected] = React.useState<boolean>(false)
  const [selectedArticles, setSelectedArticles] = React.useState<string[]>([])
  const [articlesBySite, setArticlesBySite] = React.useState<boolean>(true)
  const [articlesByCategory, setArticlesByCategory] = React.useState<boolean>(false)

  const [selectedCategories, setSelectedCategories] = React.useState<StoreActiveCategories[]>([])

  const [allSitesSelected, setAllSitesSelected] = React.useState<boolean>(true)
  const [selectedSites, setSelectedSites] = React.useState<string[]>([])
  const allSites = React.useMemo(() => sites.map(site => site.id), [sites])
  const [selectedStockStatus, setSelectedStockStatus] = React.useState<string[]>([STOCK_STATUS_ALL])
    
  const [isPending, startTransition] = React.useTransition()
  const [showFilterDialog, setShowFilterDialog] = React.useState<boolean>(false)
  const [filterDate, setFilterDate] = React.useState<string>('')
  const [filterSoldAmount, setFilterSoldAmount] = React.useState<number>(0)

  const loadPickDisplayOptions = () => {
    const savedPreference = getFromStorage<string>(PICK_DISPLAY_PREFERENCE_KEY)
    
    if (savedPreference) {
      return PICK_DISPLAY_DEFAULT_OPTIONS.map(option => ({
        ...option,
        selected: option.id === savedPreference
      }))
    }
    return PICK_DISPLAY_DEFAULT_OPTIONS
  }

  const [pickDisplayOptions, setPickDisplayOptions] = React.useState(loadPickDisplayOptions)

  React.useEffect(() => {
    const activeSiteIds = sites.filter(site => site.active).map(site => site.id)
    setSelectedSites(activeSiteIds)
    setArticlesBySite(sites.length > 1)
  }, [sites])

  const selectableCategories = React.useMemo(() => {
    if (selectedSites.length === 0) return []
    if (allSitesSelected) return categories
    return categories.filter(category => {
      return category.storeIds.some(storeId => selectedSites.includes(storeId))
    })
  }, [selectedSites, allSitesSelected, categories])

  React.useEffect(() => {
    if (showPicklistDialog && selectableCategories.length > 0) {
      setArticlesByCategory(true)
      setSelectedCategories(selectableCategories)
    }
  }, [showPicklistDialog, selectableCategories])

  const activeSelectedCategories = React.useMemo(() => {
    return selectedCategories.filter(category => {
      return selectableCategories.some(selectableCategory => selectableCategory.categoryName === category.categoryName)
    })
  }, [selectedCategories, selectableCategories])

  const totalPerItem: SelectedArticles = React.useMemo(() => {
    return groupAndFilterItems({
      items,
      selectedSites,
      selectedCategories: activeSelectedCategories.length === 0 ? [] : activeSelectedCategories,
      filterDate,
      filterSoldAmount,
      stockStatusFilter: selectedStockStatus.length === 0 ? undefined : selectedStockStatus,
    })
  }, [selectedSites, activeSelectedCategories, filterDate, filterSoldAmount, selectedStockStatus, items])

  const countFor = React.useCallback((stockStatus?: string | string[] | null) => {
    const result = groupAndFilterItems({
      items,
      selectedSites,
      selectedCategories: activeSelectedCategories.length === 0 ? [] : activeSelectedCategories,
      filterDate,
      filterSoldAmount,
      stockStatusFilter: stockStatus,
    })

    if (Array.isArray(result)) return result.length
    return Object.values(result).reduce((sum, arr) => sum + arr.length, 0)
  }, [items, selectedSites, activeSelectedCategories, filterDate, filterSoldAmount])

  const countAll = React.useMemo(() => countFor(undefined), [countFor])
  const countRunningOut = React.useMemo(() => countFor(STOCK_STATUS_RUNNING_OUT), [countFor])
  const countOutOfStock = React.useMemo(() => countFor(STOCK_STATUS_OUT), [countFor])

  const allArticles = React.useMemo(() => {
    return Array.isArray(totalPerItem)
      ? totalPerItem.map(item => item.ean)
      : Object.values(totalPerItem).flatMap(items => items.map(item => item.ean))
  }, [totalPerItem])

  React.useEffect(() => {
    if (allArticlesSelected && !arraysEqualContent(selectedArticles, allArticles)) {
      setSelectedArticles(allArticles)
    } else {
      const filtered = selectedArticles.filter(ean => allArticles.includes(ean))
      if (!arraysEqualContent(filtered, selectedArticles)) {
        setSelectedArticles(filtered)
      }
    }
  }, [allArticles, selectedArticles, allArticlesSelected])

  const handlePickDisplayChange = (e: SortingChangedEvent) => {
    const newOptions = e.detail
    setPickDisplayOptions(newOptions)
    
    const selectedOption = newOptions.find((option: PickDisplayOption) => option.selected)
    if (selectedOption) {
      setToStorage(PICK_DISPLAY_PREFERENCE_KEY, selectedOption.id)
    }
  }

  const handleToggleArticlesBySite = () => {
    startTransition(() => {
      setArticlesBySite(prev => !prev)
    })
  }

  const handleToggleArticlesByCategory = () => {
    startTransition(() => {
      setArticlesByCategory(prev => !prev)
    })
  }

  const toggleSelection = React.useCallback(<T,>(item: T, selectedItems: T[], allItems: T[], setAllSelected: (selected: boolean) => void): T[] => {
    const updatedSelection = selectedItems.includes(item)
      ? selectedItems.filter((i) => i !== item)
      : [...selectedItems, item]

    if(updatedSelection.length === allItems.length) setAllSelected(true)
    return updatedSelection
  }, [])

  const handleSelectAll = <T,>( allItems: T[], areAllSelected: boolean, setSelected: (items: T[]) => void, setAllSelected: (selected: boolean) => void ) => {
    setSelected(areAllSelected ? [] : allItems)
    setAllSelected(!areAllSelected)
  }

  const handleSelectAllSites = React.useCallback(() => {
    handleSelectAll(allSites, allSitesSelected, setSelectedSites, setAllSitesSelected)
  }, [allSites, allSitesSelected])

  const handleSiteSelection = React.useCallback((storeId: string) => {
    if (allSitesSelected) setAllSitesSelected(false)
    setSelectedSites((prevSelected) => toggleSelection(storeId, prevSelected, allSites, setAllSitesSelected))
  }, [allSitesSelected, toggleSelection, allSites])

  const handleSelectAllArticles = React.useCallback(() => {
    handleSelectAll(allArticles, allArticlesSelected, setSelectedArticles, setAllArticlesSelected)
  }, [allArticles, allArticlesSelected])

  const handleArticleSelection = React.useCallback((articleEan: string) => {
    if (allArticlesSelected) {
      setAllArticlesSelected(false)
    }
    setSelectedArticles((prevSelected) => toggleSelection(articleEan, prevSelected, allArticles, setAllArticlesSelected))
  }, [allArticlesSelected, toggleSelection, allArticles])

  const onPrint = () => {
    setTimeout(() => {
      window.print()
    }, 100)
  }

  const closeDialog = (event: Event) => {
    // Only close dialog when click has source from dialog. Needed to avoid closing when using IcaSortingDropDown inside the dialog.
    const target = event.target as HTMLElement | null
    const shouldCloseDialog = target?.localName === 'tgdb-ica-dialog' || target?.localName === 'tgdb-ica-header'
    if (shouldCloseDialog) {
      setShowPicklistDialog(false)
      setSelectedArticles([])
      setSelectedSites(sites.map((site) => site.id))
      setAllArticlesSelected(false)
      setAllSitesSelected(true)
      setArticlesBySite(true)
      setArticlesByCategory(true)
      setSelectedStockStatus([STOCK_STATUS_ALL])
      setFilterDate('')
      setSelectedCategories([])
    }
  }

  const renderStockButton = (value: string, count: number) => {
    const selected = value === STOCK_STATUS_ALL
      ? selectedStockStatus.length === 0 || selectedStockStatus.includes(STOCK_STATUS_ALL)
      : selectedStockStatus.includes(value)

    const onStockStatusSelection = () => {
      if (value === STOCK_STATUS_ALL) {
        setSelectedStockStatus([STOCK_STATUS_ALL])
        return
      }

      setSelectedStockStatus(prev => {
        if (prev.includes(value)) {
          return prev.filter(v => v !== value)
        }
        const withoutAll = prev.filter(v => v !== STOCK_STATUS_ALL)
        return [...withoutAll, value]
      })
    }

    return (
      <IcaButton
        text={`${value} (${count})`}
        onClick={onStockStatusSelection}
        variation={selected ? 'primary' : 'secondary'}
        style={
          selected
            ? ({ ['--IcaButton__background-color' as any]: 'var(--color-active-red)' } as React.CSSProperties)
            : undefined
        }
        small
      />
    )
  }

  return (
    <>
      <div className='screenView'>
        <IcaDialog
          open={showPicklistDialog}
          onClose={(e: Event) => {closeDialog(e)}}
          slot='main'
          snaptotop
          desktopfullheight
          disableBackdropClose={deviceState.isMobile ? false : true}
        >
          <IcaHeader
            slot='header'
            variant='dialog'
            heading={showFilterDialog ? 'Filtrera' : 'Plocklista'}
            leftaction={showFilterDialog ? 'back' : 'close'}
            leftlabel={showFilterDialog ? 'Tillbaka' : ''}
            onLeftactionclick={showFilterDialog ? () => setShowFilterDialog(false) : (e: Event) => closeDialog(e)}
            actions={!showFilterDialog ? JSON.stringify([{ icon: 'send', label: activeSelectedCategories.length > 0
              && activeSelectedCategories.length < selectableCategories.length
              ? `Filtrera (${activeSelectedCategories.length})`
              : 'Filtrera',
            id: 'action',
            }])
              : undefined}
            onRightactionclick={showFilterDialog ? undefined : () => setShowFilterDialog(true)}
          />

          {loading &&
          <div className='grid place-content-center mt-[30vh]'>
            <SpinnerIcon />
          </div>
          }

          {!showFilterDialog && !loading && (
            <>
              <div className='bg-[#F9F5F5]'>
                <SiteSelector
                  sites={sites}
                  selectedSites={selectedSites}
                  allSitesSelected={allSitesSelected}
                  handleSiteSelection={handleSiteSelection}
                  handleSelectAllSites={handleSelectAllSites}
                />
                <div className='flex items-center justify-between p-3 bg-white border-[#EDEDED]'>
                  <InputCheckbox
                    label='Visa per site'
                    id='display-items-per-site'
                    checked={articlesBySite}
                    onCheckChanged={handleToggleArticlesBySite}
                    className='flex gap-2'
                  />
                  <InputCheckbox
                    label='Visa per kategori'
                    id='display-items-per-category'
                    checked={articlesByCategory}
                    onCheckChanged={handleToggleArticlesByCategory}
                    className='flex gap-2'
                  />
                </div>
                <div className="flex items-center gap-2 px-4 pt-2 pb-4 bg-white">
                  {renderStockButton(STOCK_STATUS_ALL, countAll)}
                  {renderStockButton(STOCK_STATUS_RUNNING_OUT, countRunningOut)}
                  {renderStockButton(STOCK_STATUS_OUT, countOutOfStock)}
                </div>
                <div className='sticky top-0 flex justify-between items-center px-4 py-1 bg-[#f9f5f5]'>
                  <IcaCheckbox checked={allArticlesSelected} onClick={handleSelectAllArticles} label={'Artiklar'}/>
                  <IcaSortingDropDown
                    options={JSON.stringify(pickDisplayOptions)}
                    position="right"
                    onSortingchanged={handlePickDisplayChange}
                    onClose={(e: CustomEvent) => e.stopPropagation()}
                  />
                </div>
                <PicklistArticle
                  totalPerItem={totalPerItem}
                  sites={sites}
                  selectedArticles={selectedArticles}
                  handleSelectArticle={handleArticleSelection}
                  isPending={isPending}
                  articlesBySite={articlesBySite}
                  pickDisplay={pickDisplayOptions.find((option) => option.selected)?.id ?? pickDisplayOptions[0].id}
                  loading={loading}
                  error={error}
                  articlesByCategory={articlesByCategory}
                  showSingleSiteMaxQuantity={selectedSites.length === 1}
                />
              </div>
              {selectedArticles.length > 0 && (
                <div className={`fixed left-1/2 transform -translate-x-1/2 box-border w-max ${deviceState.isMobile ? 'bottom-[16px]' : 'bottom-[64px]'}`}>
                  <IcaButton
                    text={`Ladda ner lista (${selectedArticles.length} markerade)`}
                    onClick={onPrint}
                    small
                  />
                </div>
              )}
            </>
          )}

          {(!loading && error) && (
            <div className='mt-12'>
              <IcaPlaceholder
                text='Någonting gick fel. Försök igen genom att ladda om sidan.'
                align='center'
              >
                <tgdb-ica-icon-error/>
              </IcaPlaceholder>
            </div>
          )}

          {!error && showFilterDialog && (
            <PicklistFilter
              selectableCategories={selectableCategories}
              selectedCategories={activeSelectedCategories}
              selectedDate={filterDate}
              filterSoldAmount={filterSoldAmount}
              closeDialog={() => setShowFilterDialog(false)}
              setSelectedCategories={setSelectedCategories}
              setFilterDate={setFilterDate}
              setFilterSoldAmount={setFilterSoldAmount}
              setArticlesByCategory={setArticlesByCategory}
            />
          )}
        
        </IcaDialog>
      </div>

      {/* Hidden print */}
      <PrintPortal>
        <div className='printView'>
          <PrintPicklist
            selectedArticlesForPrint={getSelectedItemsForPrint(totalPerItem, selectedArticles)}
            pickDisplay={pickDisplayOptions.find((option) => option.selected)?.id ?? pickDisplayOptions[0].id}
            articlesBySite={articlesBySite}
            articlesByCategory={articlesByCategory}
            sites={sites}
            showSingleSiteMaxQuantity={selectedSites.length === 1}
          />
        </div>
      </PrintPortal>
    </>
  )
}
